// =============================================================================
// Domain Types — Single Source of Truth for all game data shapes
// =============================================================================

// ---------------------------------------------------------------------------
// Packs
// ---------------------------------------------------------------------------

export interface PackManifest {
  readonly id: string;
  readonly name: string;
  readonly version: string;
  readonly author: string;
  readonly license: string;
  readonly gameVersion: string;
  readonly description?: string;
}

/** Raw data loaded from a single pack directory before merging. */
export interface RawPackData {
  readonly id: string;
  readonly manifest: PackManifest;
  readonly blocks: BlocksPack | null;
  readonly items: ItemsPack | null;
  readonly entities: EntitiesPack | null;
  readonly players: PlayersPack | null;
  readonly vehicles: VehiclesPack | null;
  readonly recipes: RecipesPack | null;
}

// ---------------------------------------------------------------------------
// Blocks
// ---------------------------------------------------------------------------

export type BlockFace = 'top' | 'bottom' | 'left' | 'right' | 'front' | 'back';

/** Every face uses the same texture. */
export interface UniformBlockTextures {
  readonly all: string;
}

/** Per-face textures. `side` is a fallback for left/right/front/back. */
export interface FaceBlockTextures {
  readonly top?: string;
  readonly bottom?: string;
  readonly side?: string;
  readonly left?: string;
  readonly right?: string;
  readonly front?: string;
  readonly back?: string;
}

export type BlockTextures = UniformBlockTextures | FaceBlockTextures;

export type BlockShape =
  | 'cube'
  | 'slab'
  | 'stair'
  | 'panel'
  | 'pane'
  | 'fence'
  | 'carpet'
  | 'cross'
  | 'torch';

export interface BlockDefinition {
  readonly id: string;
  readonly name: string;
  readonly solid: boolean;
  readonly transparent: boolean;
  readonly hardness: number;
  readonly textures: BlockTextures | null;
  readonly shape?: BlockShape;
  readonly interactive?: boolean;
}

export interface BlocksPack {
  readonly atlas: string;
  readonly atlasSize: { readonly width: number; readonly height: number };
  readonly tileSize: number;
  readonly blocks: readonly BlockDefinition[];
}

// ---------------------------------------------------------------------------
// Items
// ---------------------------------------------------------------------------

export type ItemType = 'weapon' | 'tool' | 'ammo' | 'consumable' | 'material';

export type ProjectileVisualKind =
  | 'bullet'
  | 'energy'
  | 'grenade'
  | 'rocket'
  | 'melee';

export interface ProjectileVisualDefinition {
  readonly kind: ProjectileVisualKind;
  readonly model?: string;
  readonly texture?: string;
  readonly trailTexture?: string;
  readonly impactTexture?: string;
  readonly color?: readonly [number, number, number];
  readonly scale?: number;
  readonly impactScale?: number;
  readonly gravity?: number;
}

export interface WeaponScopeDefinition {
  readonly zoomFov: number;
  readonly sensitivityMultiplier: number;
  readonly spreadMultiplier?: number;
}

export interface ItemDefinition {
  readonly id: string;
  readonly name: string;
  readonly type: ItemType;
  readonly icon: string;
  readonly model?: string;
  readonly projectileVisual?: ProjectileVisualDefinition;
  readonly description?: string;
  readonly craftable?: boolean;

  // Combat stats
  readonly damage?: number;
  readonly fireRate?: number;
  readonly reloadTime?: number;
  readonly heatPerShot?: number;
  readonly cooldownStyle?: string;
  readonly range?: number;
  readonly ammoType?: string;
  readonly projectileCount?: number;
  readonly spread?: number;
  readonly chargeTime?: number;
  readonly piercing?: boolean;
  readonly scope?: WeaponScopeDefinition;
  readonly knockback?: number;
  readonly aoeRadius?: number;

  // Tool stats
  readonly miningPower?: number;
  readonly speed?: number;
  readonly durability?: number;
  readonly energyPerUse?: number;

  // Stack / consumable stats
  readonly stackSize?: number;
  readonly healAmount?: number;
  readonly useTime?: number;
}

export interface ItemsPack {
  readonly items: readonly ItemDefinition[];
}

// ---------------------------------------------------------------------------
// Entities (skins + mobs)
// ---------------------------------------------------------------------------

export interface SkinDefinition {
  readonly id: string;
  readonly name: string;
  readonly texture: string;
  readonly cosmeticOnly?: boolean;
  readonly model?: string;
  readonly preview?: string;
}

export interface PlayersPack {
  readonly defaultSkinId: string;
  readonly skins: readonly SkinDefinition[];
}

export interface DropEntry {
  readonly item: string;
  readonly chance: number;
  readonly min: number;
  readonly max: number;
}

export interface MobDefinition {
  readonly id: string;
  readonly name: string;
  readonly model: string;
  readonly health: number;
  readonly speed: number;
  readonly damage: number;
  readonly behavior: string;
  readonly drops: readonly DropEntry[];
}

export interface EntitiesPack {
  readonly playerModel: string;
  readonly skins: readonly SkinDefinition[];
  readonly mobs: readonly MobDefinition[];
}

// ---------------------------------------------------------------------------
// Vehicles
// ---------------------------------------------------------------------------

export type VehicleClass = 'car' | 'kart' | 'armored' | 'utility' | 'helicopter';

export interface VehicleDefinition {
  readonly id: string;
  readonly name: string;
  readonly class: VehicleClass;
  readonly model: string;
  readonly icon: string;
  readonly scale: number;
  readonly modelYawOffset: number;
  readonly groundOffset: number;
  readonly footprintRadius: number;
  readonly bodyHeight: number;
  readonly wheelBase: number;
  readonly trackWidth: number;
  readonly wheelRadius: number;
  readonly tireGrip: number;
  readonly driftGrip: number;
  readonly corneringStiffness: number;
  readonly driveBias: number;
  readonly maxSpeed: number;
  readonly reverseSpeed: number;
  readonly acceleration: number;
  readonly braking: number;
  readonly drag: number;
  readonly turnSpeed: number;
  readonly grip: number;
  readonly seatHeight: number;
  readonly cameraHeight: number;
  readonly cameraDistance: number;
  readonly cameraTargetHeight: number;
  readonly cameraLookAhead: number;
  readonly enterRadius: number;
  readonly passengerCapacity: number;
  readonly armor: number;
}

export interface VehiclesPack {
  readonly vehicles: readonly VehicleDefinition[];
}

// ---------------------------------------------------------------------------
// Recipes
// ---------------------------------------------------------------------------

export interface RecipeInput {
  readonly item: string;
  readonly count: number;
}

export interface RecipeDefinition {
  readonly id: string;
  readonly output: { readonly item: string; readonly count: number };
  readonly input: readonly RecipeInput[];
  readonly station: string;
}

export interface RecipesPack {
  readonly recipes: readonly RecipeDefinition[];
}

// ---------------------------------------------------------------------------
// Merged Game Data (computed at runtime from packs)
// ---------------------------------------------------------------------------

/** Numeric ID for the air block, reserved as the uninhabited voxel value. */
export const AIR_BLOCK_ID = 0;

export interface MergedGameData {
  readonly atlas: string | null;
  readonly tileSize: number;
  readonly atlasSize: { readonly width: number; readonly height: number };
  readonly playerModel: string | null;
  readonly defaultSkinId: string | null;
  readonly blocks: Readonly<Record<string, BlockDefinition>>;
  readonly items: Readonly<Record<string, ItemDefinition>>;
  readonly skins: Readonly<Record<string, SkinDefinition>>;
  readonly mobs: Readonly<Record<string, MobDefinition>>;
  readonly vehicles: Readonly<Record<string, VehicleDefinition>>;
  readonly recipes: Readonly<Record<string, RecipeDefinition>>;
  /** Pack id -> uint8 numeric id (air is always 0; rest are sequential). */
  readonly numericIds: Readonly<Record<string, number>>;
  /** uint8 numeric id -> pack id. */
  readonly stringIds: Readonly<Record<number, string>>;
}

// ---------------------------------------------------------------------------
// Voxel World
// ---------------------------------------------------------------------------

/** Size of a chunk along X and Z (horizontal). */
export const CHUNK_SIZE_X = 16;
export const CHUNK_SIZE_Z = 16;
/** Maximum world height (vertical). */
export const WORLD_HEIGHT = 256;
/** Length of one chunk's voxel buffer. */
export const CHUNK_VOXEL_COUNT = CHUNK_SIZE_X * WORLD_HEIGHT * CHUNK_SIZE_Z;

/** Integer chunk coordinate. */
export interface ChunkCoord {
  readonly cx: number;
  readonly cz: number;
}

/** Integer world coordinate. */
export interface WorldBlockPos {
  readonly x: number;
  readonly y: number;
  readonly z: number;
}

/** Sub-block precision: a hit from raycasting. */
export interface BlockHit {
  /** The world coordinate of the block that was hit. */
  readonly block: WorldBlockPos;
  /** The world coordinate to use for placing a new adjacent block. */
  readonly placeAdjacent: WorldBlockPos;
  /** Surface normal pointing OUT of the hit block (used for placement). */
  readonly normal: { readonly x: number; readonly y: number; readonly z: number };
}

// ---------------------------------------------------------------------------
// Texture / UV
// ---------------------------------------------------------------------------

export interface UVCoords {
  readonly u1: number;
  readonly v1: number;
  readonly u2: number;
  readonly v2: number;
}

export type BlockFaceUVs = {
  readonly [K in BlockFace]: UVCoords | null;
};

export interface AtlasUVMap {
  readonly [textureName: string]: UVCoords;
}

export interface AtlasConfig {
  readonly tileSize: number;
  readonly width: number;
  readonly height: number;
}

// ---------------------------------------------------------------------------
// Game Modes
// ---------------------------------------------------------------------------

export type GameModeId =
  | 'creative'
  | 'survival'
  | 'adventure'
  | 'freePlay'
  | 'driving'
  | 'lobby'
  | 'arenaPvp'
  | 'teamPvp'
  | 'battleRoyale'
  | 'buildFight';

export type WorldEditing = 'none' | 'mineOnly' | 'placeOnly' | 'mineAndPlace' | 'limitedBuild';
export type InventoryModel = 'creative' | 'survivalStacks' | 'adventureLoadout' | 'matchLoadout' | 'battleRoyalePickups';
export type WorldPersistence = 'persistent' | 'session' | 'matchEphemeral' | 'lobby';
export type CombatStyle = 'none' | 'pve' | 'pvp' | 'teamPvp' | 'battleRoyale';
export type BlockDropStyle = 'none' | 'directDrop' | 'toolScaledDrop';
export type CraftingStyle = 'disabled' | 'basic' | 'full';
export type DamageStyle = 'disabled' | 'environmentOnly' | 'playersAndMobs';
export type RespawnStyle = 'instant' | 'spawnWave' | 'limitedLives' | 'elimination';

export type GraphicsQuality = 'low' | 'medium' | 'high' | 'ultra';
export type CameraViewMode = 'fpv' | 'tpv';

export interface GameSettings {
  readonly quality: GraphicsQuality;
  readonly renderDistance: number;
  readonly resolutionScale: number;
  readonly maxFps: number;
  readonly showFps: boolean;
  readonly showDiagnostics: boolean;
  readonly footView: CameraViewMode;
  readonly vehicleView: CameraViewMode;
}

export const DEFAULT_GAME_SETTINGS: GameSettings = {
  quality: 'high',
  renderDistance: 3,
  resolutionScale: 1.25,
  maxFps: 45,
  showFps: false,
  showDiagnostics: false,
  footView: 'fpv',
  vehicleView: 'tpv',
};

/** Data-driven rule profile for a single game mode. */
export interface ModeRules {
  readonly id: GameModeId;
  readonly name: string;
  readonly worldEditing: WorldEditing;
  readonly inventoryModel: InventoryModel;
  readonly worldPersistence: WorldPersistence;
  readonly combat: CombatStyle;
  readonly blockDrops: BlockDropStyle;
  readonly crafting: CraftingStyle;
  readonly damage: DamageStyle;
  readonly respawn: RespawnStyle;
  /** Optional allowlist for placeable/breakable blocks (adventure / limitedBuild). */
  readonly allowedBlocks?: readonly string[];
  /** Optional allowlist for tools usable in adventure / match modes. */
  readonly allowedTools?: readonly string[];
}

/** Returns true when the player can break blocks in this mode. */
export function canBreakBlock(rules: ModeRules, blockId: string): boolean {
  if (rules.worldEditing === 'none' || rules.worldEditing === 'placeOnly') {
    return false;
  }
  if (rules.allowedBlocks !== undefined && !rules.allowedBlocks.includes(blockId)) {
    return false;
  }
  return true;
}

/** Returns true when the player can place blocks in this mode. */
export function canPlaceBlock(rules: ModeRules, blockId: string): boolean {
  if (rules.worldEditing === 'none' || rules.worldEditing === 'mineOnly') {
    return false;
  }
  if (rules.allowedBlocks !== undefined && !rules.allowedBlocks.includes(blockId)) {
    return false;
  }
  return true;
}

/** Returns true when the hotbar should be visible (blocks OR weapons). */
export function shouldShowHotbar(rules: ModeRules): boolean {
  // Show for block-editing modes
  if (rules.worldEditing !== 'none') {
    return true;
  }
  // Show weapon loadout for combat modes even when editing is disabled
  if (rules.inventoryModel === 'matchLoadout' || rules.inventoryModel === 'battleRoyalePickups') {
    return true;
  }
  return false;
}

/** Returns true when an inventory panel should be toggleable. */
export function shouldShowInventory(rules: ModeRules): boolean {
  return rules.inventoryModel !== 'matchLoadout' && rules.inventoryModel !== 'battleRoyalePickups';
}

/** Returns which inventory layout to render. */
export function inventoryLayout(rules: ModeRules): 'creative' | 'survival' | 'adventure' {
  if (rules.inventoryModel === 'creative') {
    return 'creative';
  }
  if (rules.inventoryModel === 'survivalStacks') {
    return 'survival';
  }
  return 'adventure';
}

// ---------------------------------------------------------------------------
// Map Snapshot (serializable region for import/export)
// ---------------------------------------------------------------------------

export interface MapSnapshot {
  readonly name: string
  readonly author: string
  readonly mode: GameModeId
  readonly region: {
    readonly minX: number
    readonly minY: number
    readonly minZ: number
    readonly maxX: number
    readonly maxY: number
    readonly maxZ: number
  }
  /** Flat voxel array in XYZ order: index = x + z*width + y*width*length */
  readonly voxelData: Uint8Array
  readonly spawnPoints: readonly { readonly x: number; readonly y: number; readonly z: number }[]
  readonly metadata: {
    readonly createdAt: number
    readonly description: string
    readonly tags: readonly string[]
  }
}
